'use client';

import { useEffect, useRef, useState } from 'react';
import { Send, Loader2, Wrench, CheckCircle2, AlertCircle, Play, Square, Paperclip } from 'lucide-react';

// THE CHAT BOSS ASKED FOR.
//
// "In the niche I should say it's ADU, but then I would say it expands into this,
// this, this. I want to get the research done for this, this, this. So it has to be
// manual input. It cannot be buttons."
//
// Free text in, a real Beast session on the other end, and a live readout of what
// has actually been captured so Boss can see the brief filling up as he talks.

type Msg = { role: 'boss' | 'beast'; text: string; tools?: string[] };

const SECTIONS = ['business', 'services', 'niche', 'customer',
                  'geography', 'proof', 'competition', 'research'];

export function IntakeChat({ slug, path, onLaunched }:
                           { slug: string; path: string; onLaunched?: () => void }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState<string[] | null>(null);
  const [complete, setComplete] = useState(false);
  const [started, setStarted] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchErr, setLaunchErr] = useState<string | null>(null);
  const [attachNote, setAttachNote] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const readerRef = useRef<ReadableStreamDefaultReader | null>(null);

  // STOP: cancel our stream read AND tell the gateway session to abort the
  // run — dropping only the reader would leave the agent working blind.
  const stop = async () => {
    try { await readerRef.current?.cancel(); } catch { /* already closed */ }
    fetch(`/api/gw/sessions/intake-${slug}/abort`, { method: 'POST' }).catch(() => {});
    setBusy(false);
  };

  // ATTACH: files land in <project>/uploads/ through the same gateway upload
  // route the main chat uses, then we tell the intake agent what arrived so
  // it can read them into the brief (Path A archives, brand PDFs, photos…).
  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setAttachNote(null);
    const landed: string[] = [];
    for (const f of Array.from(files)) {
      if (f.size > 50 * 1024 * 1024) { setAttachNote(`${f.name} is over 50 MB — skipped.`); continue; }
      const fd = new FormData();
      fd.append('file', f);
      try {
        const r = await fetch(`/api/gw/sessions/intake-${slug}/upload`, { method: 'POST', body: fd });
        const d = await r.json().catch(() => ({}));
        if (r.ok && (d.path || d.ok)) landed.push(d.path || f.name);
        else setAttachNote(`Upload failed (${f.name}): ${d.error || r.status}`);
      } catch { setAttachNote(`Upload failed (${f.name})`); }
    }
    if (landed.length) {
      send(`I attached ${landed.length} file(s) for the brief: ${landed.join(', ')} — read them and fold what matters into the brief.`);
    }
  };

  // Boss: "After I finish the brief, it should be a button. Start the stage 0.
  // And once I click, it switches into the pipeline."
  //
  // The button only exists when the brief is complete — launching Stage 0 against
  // an empty brief is the exact failure the readout beside it warns about.
  const startStage0 = async () => {
    if (launching) return;
    setLaunching(true); setLaunchErr(null);
    try {
      // Starts the RUN-LOOP pipeline — the engine the Run tab displays.
      // (Scar 2026-08-31: this used to POST /api/pipeline/run, the legacy
      // beast-run.sh stage walker. It launched fine — into an engine the Run
      // tab doesn't show, so Boss landed on "nothing is running". One button,
      // one engine: the same start the Run tab's ▷ uses.)
      const r = await fetch('/api/pipeline/loop', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, action: 'start' }),
      });
      const d = await r.json().catch(() => ({}));
      // Already running/waiting is not an error worth blocking on: the run
      // Boss wants is already in flight, so show him the pipeline anyway.
      if (!r.ok && !d?.ok && !/running|waiting|already/i.test(d?.error || '')) {
        setLaunchErr(d?.error || `http ${r.status}`);
        return;
      }
      onLaunched?.();
    } catch (e: any) {
      setLaunchErr(e?.message || String(e));
    } finally {
      setLaunching(false);
    }
  };

  const refreshBrief = async () => {
    const d = await fetch(`/api/pipeline/intake?path=${encodeURIComponent(path)}`)
      .then(r => r.json()).catch(() => null);
    if (!d?.ok) return;
    setMissing(d.missing ?? null);
    setComplete(!!d.complete);
    if (d.investigation) setStarted(true);
  };

  // The transcript is a FILE, not component state. Remounting (tab switch,
  // project switch, refresh, hot-reload) used to wipe the conversation while the
  // brief on disk stayed fine — indistinguishable from data loss to whoever is
  // typing. Load on mount and whenever the project changes.
  const loadTranscript = async () => {
    const d = await fetch(`/api/pipeline/transcript?path=${encodeURIComponent(path)}`)
      .then(r => r.json()).catch(() => null);
    setMsgs(d?.ok && Array.isArray(d.msgs) ? d.msgs : []);
  };

  useEffect(() => { loadTranscript(); refreshBrief(); /* eslint-disable-next-line */ }, [path]);
  // Scroll the TRANSCRIPT BOX, never the page. scrollIntoView walks every
  // scrollable ancestor, so on a phone the loaded transcript dragged the whole
  // page down and left the project picker at y=-135 — Boss "could not pick a
  // project", it was simply above the viewport (2026-09-13, WebKit 390px).
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [msgs]);

  const send = async (text: string) => {
    if (!text.trim() || busy) return;
    const first = !started;
    setMsgs(m => [...m, { role: 'boss', text }]);
    setInput(''); setBusy(true); setStarted(true);
    setMsgs(m => [...m, { role: 'beast', text: '', tools: [] }]);

    try {
      const resp = await fetch('/api/pipeline/intake', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, message: text, first }),
      });
      if (!resp.ok || !resp.body) {
        const e = await resp.json().catch(() => ({ error: `http ${resp.status}` }));
        setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: 'beast', text: `⚠️ ${e.error}` }; return c; });
        return;
      }
      const reader = resp.body.getReader();
      readerRef.current = reader;
      const dec = new TextDecoder();
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        // \u0001name\u0001 markers are tool calls — pull them out of the prose.
        const tools: string[] = [];
        const clean = acc.replace(/\u0001([^\u0001]+)\u0001/g, (_, n) => {
          if (!tools.includes(n)) tools.push(n); return '';
        });
        setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: 'beast', text: clean, tools }; return c; });
      }
    } catch (e: any) {
      setMsgs(m => { const c = [...m]; c[c.length - 1] = { role: 'beast', text: `⚠️ ${e?.message || e}` }; return c; });
    } finally {
      setBusy(false);
      refreshBrief();     // the brief may have grown during that turn
      // Persist AFTER the stream settles: saving mid-stream would write dozens
      // of partial-token versions of the same message.
      setMsgs(m => {
        fetch('/api/pipeline/transcript', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path, msgs: m }),
        }).catch(() => {});   // a failed save must never break the chat
        return m;
      });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-3">
      {/* the conversation */}
      <div className="flex flex-col rounded-xl bg-black/30 ring-1 ring-white/10 overflow-hidden">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wider text-gray-500
                        border-b border-white/5 flex items-center justify-between">
          <span>Research brief · {slug}</span>
          <span className="text-gray-600 normal-case">Stage -1 · intake</span>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3 max-h-[60vh] lg:max-h-[520px]">
          {msgs.length === 0 && (
            <div className="text-xs text-gray-500 leading-relaxed">
              Tell Beast what you&apos;re building. Free text — the niche, what it expands
              into, what to exclude, what you want researched.
              <div className="mt-2 text-gray-600 italic">
                e.g. &ldquo;Niche is ADU. Expands into garden suites, laneway homes,
                basement conversions. GTA only. Skip mobile homes.&rdquo;
              </div>
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={m.role === 'boss' ? 'flex justify-end' : ''}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs leading-relaxed whitespace-pre-wrap ${
                m.role === 'boss'
                  ? 'bg-cyan-500/15 text-cyan-100 ring-1 ring-cyan-500/25'
                  : 'bg-white/5 text-gray-200'}`}>
                {m.text || (busy && i === msgs.length - 1
                  ? <span className="flex items-center gap-1.5 text-gray-500">
                      <Loader2 className="h-3 w-3 animate-spin" />thinking…</span>
                  : null)}
                {!!m.tools?.length && (
                  <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                    {m.tools.map(t => (
                      <span key={t} className="flex items-center gap-1 text-[10px]
                                               text-amber-300/80 bg-amber-500/10 rounded px-1.5 py-0.5">
                        <Wrench className="h-2.5 w-2.5" />{t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {attachNote && <div className="px-3 py-1 text-[11px] text-amber-300/90">{attachNote}</div>}
        <div className="p-2 border-t border-white/5 flex gap-2">
          <input ref={fileRef} type="file" multiple className="hidden"
                 onChange={e => { onFiles(e.target.files); e.target.value = ''; }} />
          <button onClick={() => fileRef.current?.click()} title="Attach files — they land in the project's uploads/"
            className="px-2.5 rounded-lg bg-white/5 text-gray-400 hover:text-gray-200 hover:bg-white/10">
            <Paperclip className="h-4 w-4" />
          </button>
          <textarea
            value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
            }}
            rows={2} disabled={busy}
            placeholder="Type freely — shift+enter for a new line"
            className="flex-1 bg-white/5 rounded-lg px-3 py-2 text-xs text-gray-200
                       ring-1 ring-white/10 focus:outline-none focus:ring-cyan-500/40
                       resize-none disabled:opacity-50" />
          {busy ? (
            <button onClick={stop} title="Stop — aborts the agent's current turn"
              className="px-3 rounded-lg bg-red-500/20 text-red-300 hover:bg-red-500/30">
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => send(input)} disabled={!input.trim()}
              className="px-3 rounded-lg bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30
                         disabled:opacity-30">
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* what has actually been captured — the receipt */}
      <div className="rounded-xl bg-black/30 ring-1 ring-white/10 p-3">
        <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-3">
          Captured so far
        </div>
        {missing === null ? (
          <div className="text-xs text-gray-600">Nothing yet — start the conversation.</div>
        ) : (
          <>
            <div className="space-y-1.5">
              {SECTIONS.map(s => {
                const done = !missing.includes(s);
                return (
                  <div key={s} className="flex items-center gap-2 text-xs">
                    {done
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      : <AlertCircle className="h-3.5 w-3.5 text-gray-700 shrink-0" />}
                    <span className={done ? 'text-gray-300' : 'text-gray-600'}>{s}</span>
                  </div>
                );
              })}
            </div>
            <div className={`mt-3 rounded-lg px-2.5 py-2 text-[11px] ${
              complete ? 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/25'
                       : 'bg-amber-500/10 text-amber-300/90 ring-1 ring-amber-500/20'}`}>
              {complete
                ? 'Brief complete — Stage 0 can start.'
                : `${missing.length} section${missing.length === 1 ? '' : 's'} still empty. Stage 0 would research blind.`}
            </div>
            {complete && (
              <button onClick={startStage0} disabled={launching}
                className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg
                           px-3 py-2.5 text-xs font-semibold transition-colors
                           bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-500/40
                           hover:bg-emerald-500/30 disabled:opacity-40">
                {launching
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Launching…</>
                  : <><Play className="h-3.5 w-3.5" />Start Stage 0</>}
              </button>
            )}
            {launchErr && (
              <div className="mt-2 rounded-lg bg-red-500/10 ring-1 ring-red-500/25
                              px-2.5 py-2 text-[11px] text-red-300">
                {launchErr}
              </div>
            )}
            <div className="mt-2 text-[10px] text-gray-600 font-mono break-all">
              blueprint/investigation.json
            </div>
          </>
        )}
      </div>
    </div>
  );
}
